import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
// ---------- Source ----------
export const RecipeSourceKindSchema = z.enum(["manual", "url", "text", "ocr", "photo"]);
// ---------- Status ----------
// `pending` = an unconfirmed draft (photo-import extraction), hidden from the
// library/search/pickers and the meal planner until the user confirms it in
// review; TTL'd if abandoned. Everything else is `active`. Legacy rows without
// a status default to `active`.
export const RecipeStatusSchema = z.enum(["active", "pending"]);
export const RecipeSourceSchema = z
    .object({
    kind: RecipeSourceKindSchema,
    url: z.string().url("source.url must be a valid URL").optional(),
    fetchedAt: ISODateSchema.optional(),
})
    .passthrough();
// ---------- Ingredient ----------
export const CanonicalIngredientSchema = z
    .object({
    qty: z.number().nonnegative(),
    unit: z.enum(["g", "ml", "count", "dash"]),
    convertible: z.boolean(),
})
    .passthrough();
export const RecipeIngredientSchema = z
    .object({
    id: ULIDSchema("ing"),
    raw: z.string().min(1, "raw is required").max(500, "raw must be ≤ 500 chars"),
    qty: z.number().nonnegative("qty must be ≥ 0").nullable(),
    unit: z.string().max(32, "unit must be ≤ 32 chars").nullable(),
    name: z.string().min(1, "name is required").max(200, "name must be ≤ 200 chars"),
    notes: z.string().max(500).nullable(),
    // Persisted on imported recipes (import-worker), absent on manual ones.
    canonical: CanonicalIngredientSchema.nullable().optional(),
})
    .passthrough();
// ---------- Step ----------
export const StepKindSchema = z.enum(["active", "prep", "wait"]);
export const StepTimerSchema = z
    .object({
    label: z.string().max(40, "timer label must be ≤ 40 chars"),
    minutes: z.number().int("minutes must be an integer").positive("minutes must be > 0").max(10080),
})
    .passthrough();
export const RecipeStepSchema = z
    .object({
    id: ULIDSchema("step"),
    text: z.string().min(1, "text is required").max(4000, "text must be ≤ 4000 chars"),
    // First-class cooking fields (Plan 06a). Defaults make pre-Plan-06a rows parse:
    // imported rows already carry kind/timers/estimatedMin as passthrough attributes;
    // parallelWith is new so it defaults to []. parallelWith holds the *step ids* (not
    // indices) of steps that can run concurrently; dangling refs are tolerated by readers.
    kind: StepKindSchema.default("active"),
    timers: z.array(StepTimerSchema).max(20, "≤ 20 timers per step").default([]),
    estimatedMin: z.number().int("estimatedMin must be an integer").positive("estimatedMin must be > 0").max(10080).nullable().default(null),
    parallelWith: z.array(ULIDSchema("step")).max(50, "≤ 50 parallel refs").default([]),
    // Imported step photos (photo ids; thumb/full resolved via the CDN, same as
    // user-upload photos). `ULIDSchema("ph")` mirrors PhotoKeySchema — kept local
    // here to avoid a recipe.ts ↔ photo.ts import cycle.
    imageKeys: z.array(ULIDSchema("ph")).default([]),
})
    .passthrough();
// ---------- Photos ----------
export const RecipePhotosSchema = z
    .object({
    coverKey: z.string().nullable(),
    extras: z.array(z.string()),
})
    .strict();
// ---------- Taxonomy refs ----------
export const RecipeTaxonomyRefsSchema = z
    .object({
    tagIds: z.array(ULIDSchema("tag")),
    categoryIds: z.array(ULIDSchema("category")),
    cuisineId: ULIDSchema("cuisine").nullable(),
    dietFlags: z.array(ULIDSchema("diet")),
})
    .passthrough();
// ---------- Plan 08 content fields ----------
const DescriptionSchema = z.string().max(2048, "description must be ≤ 2048 chars").nullable();
const NotesSchema = z.string().max(10240, "notes must be ≤ 10240 chars").nullable();
const ServingsSchema = z
    .number()
    .int("servings must be an integer")
    .min(1, "servings must be ≥ 1")
    .max(999, "servings must be ≤ 999")
    .nullable();
const TimeMinSchema = z
    .number()
    .int("must be an integer")
    .min(1, "must be ≥ 1 minute")
    .max(10080, "must be ≤ 10080 minutes (1 week)")
    .nullable();
const DifficultySchema = z.enum(["easy", "medium", "hard"]).nullable();
const RatingSchema = z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.null(),
]);
// Cross-field check used by every schema that may carry both times.
function activeNotGreaterThanTotal(v) {
    if (v.totalTimeMin == null || v.activeTimeMin == null)
        return true;
    return v.activeTimeMin <= v.totalTimeMin;
}
const TIME_RANGE_ERROR = {
    message: "activeTimeMin must be ≤ totalTimeMin",
    path: ["activeTimeMin"],
};
// ---------- Stats (Plan 07a) ----------
const RatingAggSchema = z.object({
    avg: z.number().min(0).max(5).nullable().default(null),
    count: z.number().int().min(0).default(0),
});
const ByStepStatSchema = z.object({
    avgMin: z.number().min(0),
    count: z.number().int().min(0),
    parallelRunsExcluded: z.number().int().min(0).default(0),
});
export const StatsSchema = z
    .object({
    cookCount: z.number().int().min(0).default(0),
    avgActiveMin: z.number().min(0).nullable().default(null),
    avgElapsedMin: z.number().min(0).nullable().default(null),
    byStep: z.record(z.string(), ByStepStatSchema).default({}),
    dishRating: RatingAggSchema.default({ avg: null, count: 0 }),
    cookRating: RatingAggSchema.default({ avg: null, count: 0 }),
    lastComputedAt: ISODateSchema.nullable().default(null),
    lastCookedAt: ISODateSchema.nullable().default(null),
})
    .passthrough();
// ---------- Full DDB row ----------
const TitleSchema = z
    .string()
    .min(1, "title is required")
    .max(200, "title must be ≤ 200 chars")
    .refine((s) => s.trim().length > 0, { message: "title must not be whitespace-only" });
const Gsi1SkSchema = z
    .string()
    .refine((v) => {
    const hash = v.indexOf("#");
    if (hash < 1)
        return false;
    const iso = v.slice(0, hash);
    const ref = v.slice(hash + 1);
    return ISODateSchema.safeParse(iso).success && ULIDSchema("rec").safeParse(ref).success;
}, { message: "gsi1sk must be '<isoTimestamp>#<recipeId>'" });
export const RecipeRowSchema = z
    .object({
    householdId: ULIDSchema("hh"),
    sk: ULIDSchema("rec"),
    gsi1pk: ULIDSchema("hh"),
    gsi1sk: Gsi1SkSchema,
    id: ULIDSchema("rec"),
    schemaVersion: z.literal(1),
    version: z.number().int().min(1),
    type: z.literal("Recipe"),
    title: TitleSchema,
    source: RecipeSourceSchema,
    status: RecipeStatusSchema.default("active"),
    ingredients: z.array(RecipeIngredientSchema),
    steps: z.array(RecipeStepSchema),
    taxonomy: RecipeTaxonomyRefsSchema,
    photos: RecipePhotosSchema.default({ coverKey: null, extras: [] }),
    description: DescriptionSchema.default(null),
    notes: NotesSchema.default(null),
    servings: ServingsSchema.default(null),
    totalTimeMin: TimeMinSchema.default(null),
    activeTimeMin: TimeMinSchema.default(null),
    difficulty: DifficultySchema.default(null),
    rating: RatingSchema.default(null),
    stats: StatsSchema.nullable().default(null),
    // User intent flag (Plan 13): bookmark a recipe as "want to try". Absent =
    // not flagged (old rows need no migration). Auto-cleared on first cook
    // (Plan 13 Task 3).
    wantToTry: z.boolean().optional(),
    createdBy: UserIdSchema,
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    deletedAt: ISODateSchema.nullable(),
    // ttl is set on soft delete AND on pending photo-import drafts (7-day expiry);
    // absent on confirmed live rows. Passthrough tolerates either way.
})
    .passthrough()
    .refine(activeNotGreaterThanTotal, TIME_RANGE_ERROR);
// ---------- API shapes ----------
// POST /recipes body. `strict` rejects server-controlled keys (version, id, createdAt, …).
export const CreateRecipeRequestSchema = z
    .object({
    title: TitleSchema,
    source: RecipeSourceSchema,
    status: RecipeStatusSchema.optional(),
    ingredients: z.array(RecipeIngredientSchema),
    steps: z.array(RecipeStepSchema),
    taxonomy: RecipeTaxonomyRefsSchema,
    description: DescriptionSchema.optional(),
    notes: NotesSchema.optional(),
    servings: ServingsSchema.optional(),
    totalTimeMin: TimeMinSchema.optional(),
    activeTimeMin: TimeMinSchema.optional(),
    difficulty: DifficultySchema.optional(),
    rating: RatingSchema.optional(),
})
    .strict()
    .refine(activeNotGreaterThanTotal, TIME_RANGE_ERROR);
// PATCH /recipes/{id} body. `version` is mandatory (optimistic concurrency).
// At least one updatable field must be present.
export const PatchRecipeRequestSchema = z
    .object({
    version: z.number().int().min(1),
    title: TitleSchema.optional(),
    source: RecipeSourceSchema.optional(),
    // Confirming a pending photo-import draft flips status active; the handler
    // also clears the draft ttl on that flip (see patch-recipe).
    status: RecipeStatusSchema.optional(),
    ingredients: z.array(RecipeIngredientSchema).optional(),
    steps: z.array(RecipeStepSchema).optional(),
    taxonomy: RecipeTaxonomyRefsSchema.optional(),
    description: DescriptionSchema.optional(),
    notes: NotesSchema.optional(),
    servings: ServingsSchema.optional(),
    totalTimeMin: TimeMinSchema.optional(),
    activeTimeMin: TimeMinSchema.optional(),
    difficulty: DifficultySchema.optional(),
    rating: RatingSchema.optional(),
    // Absent = unchanged; both true and false must persist explicitly (Plan 13
    // Task 2), so this is `.optional()` rather than defaulted.
    wantToTry: z.boolean().optional(),
})
    .strict()
    .refine((v) => v.title !== undefined ||
    v.source !== undefined ||
    v.status !== undefined ||
    v.ingredients !== undefined ||
    v.steps !== undefined ||
    v.taxonomy !== undefined ||
    v.description !== undefined ||
    v.notes !== undefined ||
    v.servings !== undefined ||
    v.totalTimeMin !== undefined ||
    v.activeTimeMin !== undefined ||
    v.difficulty !== undefined ||
    v.rating !== undefined ||
    v.wantToTry !== undefined, {
    message: "at least one of title|source|status|ingredients|steps|taxonomy|description|notes|servings|totalTimeMin|activeTimeMin|difficulty|rating|wantToTry is required",
})
    .refine(activeNotGreaterThanTotal, TIME_RANGE_ERROR);
// API response item — RecipeRow minus the internal DDB keys (householdId, sk, gsi1pk, gsi1sk).
// `.passthrough()` so Plan 05+'s photos/stats/etc round-trip transparently.
export const RecipeApiItemSchema = z
    .object({
    id: ULIDSchema("rec"),
    schemaVersion: z.literal(1),
    version: z.number().int().min(1),
    title: TitleSchema,
    source: RecipeSourceSchema,
    status: RecipeStatusSchema.default("active"),
    ingredients: z.array(RecipeIngredientSchema),
    steps: z.array(RecipeStepSchema),
    taxonomy: RecipeTaxonomyRefsSchema,
    photos: RecipePhotosSchema.default({ coverKey: null, extras: [] }),
    description: DescriptionSchema.default(null),
    notes: NotesSchema.default(null),
    servings: ServingsSchema.default(null),
    totalTimeMin: TimeMinSchema.default(null),
    activeTimeMin: TimeMinSchema.default(null),
    difficulty: DifficultySchema.default(null),
    rating: RatingSchema.default(null),
    stats: StatsSchema.nullable().default(null),
    // See RecipeRowSchema.wantToTry — absent = not flagged.
    wantToTry: z.boolean().optional(),
    createdBy: UserIdSchema,
    createdAt: ISODateSchema,
    updatedAt: ISODateSchema,
    deletedAt: ISODateSchema.nullable(),
})
    .passthrough()
    .refine(activeNotGreaterThanTotal, TIME_RANGE_ERROR);
export const RecipeListResponseSchema = z.object({
    items: z.array(RecipeApiItemSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=recipe.js.map